import { store } from '@/store'
import { messagesActions, selectChatId } from 'store/messages'
import { captchaAxios } from 'api/config'
import ObjectID from 'bson-objectid'
import axios from 'axios'
import { UploadService } from '@runelab/odin-common'
import { settingsSelectors } from 'store/settings'
import { ChatRoleEnum, Participant } from 'types/chat'
import { getForceAgenticSearchParams } from 'utils/query-params'
import { buildAgentInputs } from 'utils/agent-inputs'
import { audioPlayer } from 'services/audio-player'

export const uploadFiles = async (files: File[]) => {
  if (files.length === 0) return []
  const service = new UploadService(axios)
  return await Promise.all(
    files.map((file) => {
      return service.uploadFile({
        file,
        // linkedEntity* non serve piu: l'aggancio e la FK sul padre (qui attachmentIds
        // sul messaggio). Il tipo di odin-common li vuole ancora, il server li ignora.
        uploadFileParam: {
          additionalInfo: {},
          linkedEntityId: '',
          linkedEntityType: '',
        },
      })
    }),
  )
}

/** chatId -> answerId of its last question, when that question was spoken: a paused agent run resumes spoken too. */
export const voiceAnswerByChat = new Map<string, string>()

type AskBotAnswerParams = {
  question: string
  questionId: string
  attachmentIds?: string[]
  /** Question came from the mic: server streams TTS audio alongside the text (ignored if the tenant has no voice flag). */
  voice?: boolean
}

export const askBotAnswer = async ({ question, questionId, attachmentIds, voice }: AskBotAnswerParams) => {
  const state = store.getState()
  const chatId = selectChatId(state)
  const answerId = String(ObjectID())

  const agenticEnabled = settingsSelectors.agenticEnabled(state)
  const requirements = settingsSelectors.agentInputRequirements(state)
  const allAgentInputs = settingsSelectors.agentInputs(state)

  // Forward every injected input the agents declared (required or optional).
  const inputs = buildAgentInputs(state)

  // Go agentic only when every REQUIRED runtime input was injected by the host;
  // missing optional inputs are fine. Otherwise fall back to plain RAG chat so the
  // call never breaks. `?forceAgentic=true` (backoffice demo) bypasses this gating.
  const forceAgentic = getForceAgenticSearchParams()
  // A `file` input is bound server-side to the attachments of the message, never injected by the host.
  const missingRequired = requirements
    .filter((req) => req.isRequired && req.type !== 'file')
    .filter((req) => allAgentInputs[req.key] == null || allAgentInputs[req.key] === '')
    .map((req) => req.key)
  // A notebook chat is locked to one agent by the server: always agentic, inputs always forwarded.
  // qlik: this project always runs agentic (supervisor + always-on Qlik agent), the plain RAG
  // endpoint is never used here, so the gating above only logs.
  const useAgentic = true

  if (agenticEnabled && missingRequired.length > 0) {
    console.warn(
      '[agentic] missing required agentInputs:',
      missingRequired,
      forceAgentic ? '(forced on)' : '(disabled)',
    )
  }

  console.debug({
    answerId,
    questionId,
    attachmentIds,
    useAgentic,
  })

  if (chatId) {
    store.dispatch(messagesActions.setChatStatus({ chatId, status: 'awaitingBot' }))
  }

  // Voice question: only this answer's TTS may play (one voice at a time).
  if (voice) {
    audioPlayer.expect(answerId)
  }
  if (chatId) {
    if (voice) voiceAnswerByChat.set(chatId, answerId)
    else voiceAnswerByChat.delete(chatId)
  }

  try {
    // Everything in the body (server >= the "question in body" release; it still accepts the query
    // string from older clients). A long question in the URL — a pasted transcript, an offer — blew
    // Node's 16 KB request-line limit and died with a 431 the browser showed as a CORS error.
    const { data } = await captchaAxios.post<{ message: string }>(`chats/${chatId}/answer/bot`, {
      question,
      questionId,
      answerId,
      attachmentIds,
      ...(useAgentic && { agentic: true, inputs }),
      ...(voice && { voice: true }),
    })

    // The backend now acks immediately and streams the answer over WS (to avoid the 60s HTTP
    // timeout), so we must NOT reset the status here: keep 'awaitingBot' (dots stay up) until the
    // first stream arrives (-> 'streaming') and end_stream flips it to 'idle' in useChatWebSocket.
    // Fallback: if the full answer still comes back in the body (non-streaming), render it and
    // reset to 'idle' ourselves, since no stream/end_stream will follow.
    if (data?.message && chatId) {
      const chat = store.getState().messageState.chats[chatId]
      const bot = chat?.participants?.find((participant: Participant) => participant.role === ChatRoleEnum.BOT)
      store.dispatch(
        messagesActions.upsertBotMessage({
          chatId,
          botMessageId: answerId,
          partialResponse: data.message,
          senderId: bot?.userId ?? 'bot',
        }),
      )
      store.dispatch(messagesActions.setChatStatus({ chatId, status: 'idle' }))
    }
  } catch (error) {
    // Ack failed: no stream will arrive, so clear the indicator here.
    if (chatId) {
      store.dispatch(messagesActions.setChatStatus({ chatId, status: 'idle' }))
    }
    return Promise.reject(error)
  }
}
